import { LogoutButton } from '@/components/buttons';
import { StandardButton } from '@/components/buttons/standard';
import { ContentPanel } from '@/components/content-panel';
import { CustomContentSection } from '@/components/custom-content-section';
import { useLayoutContext } from '@/contexts';
import { useCurrentUser } from '@/hooks';
import { ContentWithLoad } from '@/layouts/content-with-load';
import { PrimaryLayout } from '@/layouts/primary';
import { TopNavFor } from '@/layouts/primary/top-nav';
import { Affix, Flex, Transition } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef } from 'react';
import {
	ContentSectionLocation,
	ContentSectionPlacement,
} from '../../../../backend/src/types.generated';
import { AccountForm, AccountFormValues } from './form';
import { useGetUserAccountQuery, useUpdateUserAccountMutation } from './graphql.generated';

export const AccountPage = () => {
	const navigate = useNavigate();
	const { currentUser, loading: userLoading, refetchUser, logUserOut } = useCurrentUser();
	const {
		data,
		loading,
		error,
		refetch: refetchUserAccount,
	} = useGetUserAccountQuery({
		variables: { id: currentUser?.id || '' },
		skip: !currentUser?.id,
	});
	const [updateUserAccount, { loading: updatingUser }] = useUpdateUserAccountMutation();
	const { isDesktop } = useLayoutContext();

	const formRef = useRef<FormikProps<AccountFormValues>>(null);
	const submitForm = useCallback(() => {
		formRef.current?.submitForm();
	}, []);

	const saveProfileButton = useMemo(
		() => (
			<StandardButton loading={updatingUser} onClick={submitForm}>
				Save Profile
			</StandardButton>
		),
		[submitForm, updatingUser]
	);

	const cancelButton = useMemo(
		() => (
			<StandardButton variant="default" onClick={() => formRef.current?.resetForm()}>
				Cancel
			</StandardButton>
		),
		[formRef]
	);

	const logoutButton = useMemo(() => <LogoutButton />, []);

	const handleSubmit = useCallback(
		async (values: AccountFormValues) => {
			try {
				if (!currentUser?.id) {
					throw new Error('User is not authenticated');
				}

				await updateUserAccount({
					variables: {
						input: {
							formattedAddress: values.formattedAddress,
							addressLine1: values.addressLine1,
							addressLine2: values.addressLine2,
							city: values.city,
							cityPlaceId: values.cityPlaceId,
							council: values.council,
							dwellingType: values.dwellingType,
							email: values.email.toLowerCase(),
							firstName: values.firstName,
							gender: values.gender,
							householdSize: values.householdSize || null,
							isOver18: values.isOver18,
							isParticipatingInPrizeDraw: values.isParticipatingInPrizeDraw,
							lastName: values.lastName,
							phoneNumber: values.phoneNumber || null,
							postcode: values.postcode,
							state: values.state,
							lat: values.lat,
							lng: values.lng,
							isManualAddress: values.isManualAddress,
							placeId: values.placeId,
						},
					},
				});

				if (currentUser?.contact?.email?.toLowerCase() !== values.email.toLowerCase()) {
					await logUserOut();
					navigate({ to: '/welcome' });
				} else {
					await refetchUser();
					await refetchUserAccount();
				}
			} catch (error) {
				// TODO: need some error handling to give the user feedback
				console.error(error);
			}
		},
		[
			currentUser?.id,
			currentUser?.contact?.email,
			updateUserAccount,
			logUserOut,
			navigate,
			refetchUser,
			refetchUserAccount,
		]
	);

	const handleChangePassword = useCallback(() => navigate({ to: '/change-password' }), [navigate]);

	const handleDeleteAccount = useCallback(() => navigate({ to: '/delete-account' }), [navigate]);

	const initialValues: AccountFormValues = useMemo(() => {
		return {
			firstName: data?.user?.contact?.firstName || '',
			lastName: data?.user?.contact?.lastName || '',
			email: data?.user?.contact?.email?.toLowerCase() || '',
			phoneNumber: data?.user?.phoneNumber || '',
			formattedAddress: data?.user?.formattedAddress || '',
			addressLine1: data?.user?.addressLine1 || '',
			addressLine2: data?.user?.addressLine2 || '',
			city: data?.user?.city || '',
			cityPlaceId: data?.user?.cityPlaceId || null,
			postcode: data?.user?.postcode || '',
			state: data?.user?.state || '',
			gender: data?.user?.gender || '',
			dwellingType: data?.user?.dwellingType || '',
			householdSize: data?.user?.householdSize || null,
			isOver18: data?.user?.isOver18 || false,
			isParticipatingInPrizeDraw: data?.user?.isParticipatingInPrizeDraw || false,
			isManualAddress: data?.user?.isManualAddress || false,
			lat: data?.user?.lat || null,
			lng: data?.user?.lng || null,
			placeId: data?.user?.placeId || null,
		};
	}, [data?.user]);

	return (
		<PrimaryLayout
			header={{ title: 'Account', backTo: '/home', children: logoutButton }}
			topNav={TopNavFor.ACCOUNT}
			footer={{ children: saveProfileButton }}
		>
			<ContentWithLoad
				loading={loading || userLoading}
				error={!!error}
				isEmpty={!data?.user}
				isEmptyMessage="Please log in or refresh to view your account"
				isErrorMessage="An error occurred while loading your account"
			>
				<ContentPanel hasTopNav width="md" gap="md">
					<CustomContentSection
						location={ContentSectionLocation.Account}
						placement={ContentSectionPlacement.Top}
					/>
					<AccountForm formRef={formRef} onSubmit={handleSubmit} initialValues={initialValues} />
					<CustomContentSection
						location={ContentSectionLocation.Account}
						placement={ContentSectionPlacement.Middle}
					/>
					{isDesktop ? (
						<Flex gap="lg" pt="sm" w="100%" justify="center">
							<StandardButton variant="transparent" onClick={handleChangePassword}>
								Change Password
							</StandardButton>
							<StandardButton variant="danger" onClick={handleDeleteAccount}>
								Delete Account
							</StandardButton>
						</Flex>
					) : (
						<>
							<StandardButton variant="transparent" onClick={handleChangePassword}>
								Change Password
							</StandardButton>
							<StandardButton variant="danger" onClick={handleDeleteAccount}>
								Delete Account
							</StandardButton>
						</>
					)}
					<CustomContentSection
						location={ContentSectionLocation.Account}
						placement={ContentSectionPlacement.Bottom}
					/>
				</ContentPanel>
				<Transition mounted={isDesktop} transition="slide-up" duration={400} timingFunction="ease">
					{(styles) => (
						<Affix style={styles} w="100%" position={{ bottom: 0, left: 0 }}>
							<Flex w="screen" py="sm" gap="lg" bg="white" justify="center" px="xl">
								<Flex w={300}>{cancelButton}</Flex>
								<Flex w={300}>{saveProfileButton}</Flex>
							</Flex>
						</Affix>
					)}
				</Transition>
			</ContentWithLoad>
		</PrimaryLayout>
	);
};
